import { useState, useEffect, useMemo } from "react";
import toast from "react-hot-toast";
import {
  XMarkIcon,
  ArrowPathIcon
} from "@heroicons/react/24/outline";

import FileUploader from "../../common/FileUploader";
import Modal from "../../common/Modal";
import { useFetch, useSubmission } from "../../../hooks/useCallApi";

function LegislationDocumentModal({
  isOpen,
  onClose,
  document,
  version = null,
  onSuccess,
  mode = "create",
  capitalizeFirst
}) {
  const isEditMode = mode === "edit";
  const versionId = version?.version_id;

  const [formData, setFormData] = useState({
    summary: "",
    change_summary: "",
    finalVersion: false,
    is_final: false,
    file: null,
    remove_legislative_file: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [docFile, setDocFile] = useState(null)

  const { data: versionData, isLoading, refetch } = useFetch(
    ['get-version', versionId],
    api => api.admin.legislation.getVersionId(versionId),
    { enabled: !!versionId && isEditMode }
  );

  const { mutateAsync: uploadVersion } = useSubmission(
    ['upload-version'],
    (api, args) => api.admin.legislation.uploadVersion(args.id, args.payload),
    {
      onSuccess: () => {
        toast.success("Document uploaded successfully");
      },
      onError: (error) => {
        toast.error(
          error?.response?.data?.message || 'Failed to uploade document'
        )
      },
    }
  );

  const { mutateAsync: updateVersion } = useSubmission(
    ['update-version'],
    (api, args) => api.admin.legislation.updateVersion(
      args.legislativeId,
      args.versionId,
      args.data
    ),
    {
      onSuccess: () => {
        toast.success("Version updated successfully");
      },
      onError: (error) => {
        toast.error(
          error?.response?.data?.message || 'Failed to update version'
        )
      },
    }
  );

  useEffect(() => {
    if (isEditMode && versionData?.data) {
      const v = versionData.data;

      setFormData({
        summary: "",
        change_summary: v.change_summary || "",
        finalVersion: false,
        is_final: v.is_final || false,
        file: null
      });
      setDocFile(v.legislative_file || null)
    }
  }, [isEditMode, versionData]);

  const handleSubmit = async (e) => {
    e?.preventDefault();

    try {
      setIsSubmitting(true);

      const payload = new FormData();
      if (docFile?.type === 'new') {
        payload.append('legislative_file', docFile.file)
      }

      if (docFile?.type === 'existing' && docFile?.remove === 1) {
        payload.append('remove_legislative_file', 1)
      }

      if (isEditMode) {
        payload.append("change_summary", formData.change_summary || "");
        payload.append("is_final", formData.is_final ? 1 : 0);

        await updateVersion({
          legislativeId: versionData?.data?.legislative_document_id,
          versionId: versionData?.data?.id,
          data: payload
        });

      } else {
        payload.append("change_summary", formData.summary || "");
        payload.append("is_final", formData.is_final ? 1 : 0);

        await uploadVersion({
          id: document?.legislative_document_id,
          payload
        });
      }

      refetch()
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !document) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex justify-between items-center">
          <div className="flex gap-3 items-center">
            <div className="bg-gradient-to-r from-[#60A4F9] to-[#2865EC] px-3 py-2 rounded text-white">
              <h3 className="text-base text-white font-semibold">
                {capitalizeFirst(document.legislation_type)} No. {document.document_number}
              </h3>
            </div>
            <span className="text-lg font-semibold">
              {isEditMode ? "Edit Version" : "Add New Legislation Document"}
            </span>
          </div>
        </div>
      }
      footer={
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="text-gray-600">
            Cancel
          </button>

          <button
            type="button"
            disabled={isSubmitting}
            className="bg-blue-600 text-white px-6 py-2 rounded-md flex items-center gap-2"
            onClick={handleSubmit}
          >
            {isSubmitting && <ArrowPathIcon className="w-4 h-4 animate-spin" />}
            {isEditMode ? "Update Version" : "Create"}
          </button>
        </div>
      }
      size="3xl"
    >
      {isLoading ? (
        <div className="p-12 flex justify-center">
          <div className="animate-spin h-10 w-10 border-b-2 border-blue-600 rounded-full" />
        </div>
      ) : (
        <form className="p-6 space-y-6">
          <FileUploader
            label={isEditMode ? "Legislative Document File" : "Support Document"}
            inputId={isEditMode ? "legislation-file" : "support-file"}
            multiple={false}
            mode={mode}
            initialFiles={docFile ? [docFile] : []}
            onFilesChange={(files) => setDocFile(files[0] || null)}
          />

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={isEditMode ? formData.is_final : formData.finalVersion}
              onChange={(e) =>
                setFormData(prev => ({
                  ...prev,
                  [isEditMode ? "is_final" : "finalVersion"]: e.target.checked
                }))
              }
            />
            <span className="text-sm font-medium">Final Version</span>
          </label>

          <div>
            <label className="block text-sm font-medium mb-2">
              {isEditMode ? "Change Summary" : "Summary"}
            </label>
            <textarea
              rows={4}
              value={isEditMode ? formData.change_summary : formData.summary}
              onChange={(e) =>
                setFormData(prev => ({
                  ...prev,
                  [isEditMode ? "change_summary" : "summary"]: e.target.value
                }))
              }
              className="w-full border rounded-md p-3 focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </form>
      )}
    </Modal>
  );
}

export default LegislationDocumentModal;
